// Cross-domain auth utilities
// Handles token encoding/decoding for safe URL transport and localStorage encryption

const STORAGE_KEY = 'amm_session';
const ENCODE_PREFIX = 'amm_v1_';

// Simple obfuscation key derived from the origin to prevent naive XSS token extraction
const _obfKey = () => {
  const base = (typeof window !== 'undefined' ? window.location.origin : 'fallback') + STORAGE_KEY;
  let h = 0;
  for (let i = 0; i < base.length; i++) h = ((h << 5) - h + base.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
};

// Obfuscate token before storing (not cryptographic — prevents trivial theft via XSS)
function _obfuscate(str) {
  if (!str) return '';
  const key = _obfKey();
  return Array.from(str).map((c, i) =>
    String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length))
  ).join('');
}

// Simple encode/decode for URL transport (base64 + reverse + prefix)
// JWT is already signed — this just prevents raw token in browser history/referrer
export function encodeToken(token) {
  if (!token) return '';
  try {
    const reversed = token.split('').reverse().join('');
    return ENCODE_PREFIX + btoa(reversed);
  } catch {
    return '';
  }
}

export function decodeToken(encoded) {
  if (!encoded || !encoded.startsWith(ENCODE_PREFIX)) return null;
  try {
    const payload = encoded.slice(ENCODE_PREFIX.length);
    const reversed = atob(payload);
    return reversed.split('').reverse().join('');
  } catch {
    return null;
  }
}

// localStorage helpers — store session data with obfuscation
export function saveSession(token, user = null) {
  try {
    const session = { t: _obfuscate(token), user, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    // Store obfuscated token for backward-compat consumers
    localStorage.setItem('token', _obfuscate(token));
  } catch (err) { /* Non-critical: session save failed */ }
}

export function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Fallback: check old 'token' key
      const oldToken = localStorage.getItem('token');
      if (oldToken) {
        // Attempt to de-obfuscate (handles both obfuscated and legacy plaintext tokens)
        const deobf = _obfuscate(oldToken);
        // Check if it looks like a JWT (has two dots)
        const token = deobf.split('.').length === 3 ? deobf : oldToken;
        return { token, user: null };
      }
      return null;
    }
    const session = JSON.parse(raw);
    // Expire after 7 days
    if (Date.now() - session.ts > 7 * 24 * 60 * 60 * 1000) {
      clearSession();
      return null;
    }
    // De-obfuscate the token
    const storedVal = session.t || session.token;
    const deobf = _obfuscate(storedVal);
    // Check if it looks like a JWT
    const token = deobf.split('.').length === 3 ? deobf : storedVal;
    return { token, user: session.user };
  } catch {
    return null;
  }
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem('token');
}

// Get the current auth token (de-obfuscated). Use this instead of localStorage.getItem('token')
export function getToken() {
  const session = loadSession();
  return session?.token || null;
}

// URL helpers
export function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get('auth_token');
  if (!encoded) return null;

  const token = decodeToken(encoded);

  // Clean URL — remove auth_token param
  params.delete('auth_token');
  const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
  window.history.replaceState({}, '', cleanUrl);

  return token;
}

export function getRedirectParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get('redirect') || null; // 'auto', 'coupon', or null
}

// Build cross-domain URL with encoded token
export function buildAuthUrl(baseUrl, token) {
  if (!baseUrl || !baseUrl.startsWith('http')) return baseUrl;
  if (!token) return baseUrl;
  const url = new URL(baseUrl);
  url.searchParams.set('auth_token', encodeToken(token));
  return url.toString();
}
